import React, { useState, useEffect } from "react";
import { Modal, Button, Form } from "react-bootstrap";
import { createThirdpartyQuestionsService, editThirdpartyQuestionsService, getTprmAssessmentCategoriesService, get_questionnaires } from "../../services";
import ButtonLoader from "../../Components/ButtonLoader";

const RESPONSE_TYPE_OPTIONS = [
  { value: "yes_no", label: "Yes/No" },
  { value: "multi_select", label: "Multi Select" },
];

const WEIGHT_OPTIONS = [
  { value: 1, label: "1 (Lowest)" },
  { value: 2, label: "2" },
  { value: 3, label: "3" },
  { value: 4, label: "4" },
  { value: 5, label: "5 (Highest)" },
];

function CreateTPRMQuestionModal({ show, onHide, onSuccess, editingQuestion }) {
  const [loading, setLoading] = useState(false);
  const [isCustomCategory, setIsCustomCategory] = useState(false);
  const [categoryOptions, setCategoryOptions] = useState([]);
  const [allCategoryKeys, setAllCategoryKeys] = useState([]);
  const [loadingCategories, setLoadingCategories] = useState(false);
  const [questionnaireOptions, setQuestionnaireOptions] = useState([]);
  const [loadingQuestionnaires, setLoadingQuestionnaires] = useState(false);
  const [isCustomQuestionnaire, setIsCustomQuestionnaire] = useState(false);
  const [formData, setFormData] = useState({
    questionnaire_name: "",
    category: "",
    sub_category: "",
    question_text: "",
    response_type: "yes_no",
    expected_evidence: "",
    explanation: "",
    weight: 1,
    multi_select_options: [],
  });

  const [errors, setErrors] = useState({});
  const [resMsg, setResMsg] = useState("");
  const [errResMsg, setErrResMsg] = useState("");

  // Map API response type values to form values
  const responseTypeMap = {
    "Yes / No": "yes_no",
    "Yes/No": "yes_no",
    "Multi-select": "multi_select",
    "Multi Select": "multi_select",
    "Text": "text",
    "File Upload": "file_upload",
    "Rating (1-5)": "rating",
    "Rating": "rating",
  };

  // Fetch categories when modal opens
  useEffect(() => {
    const fetchCategories = async () => {
      if (!show) return;

      setLoadingCategories(true);
      try {
        const response = await getTprmAssessmentCategoriesService();
        const data = response?.statusCode?.response_data?.data;

        if (data) {
          // Get organization categories for dropdown
          const orgCategories = data.organization_categories || [];
          const options = orgCategories.map(cat => ({
            value: cat.key,
            label: cat.label
          }));
          // Add custom option at the end
          options.push({ value: "custom", label: "Custom (Enter your own)" });
          setCategoryOptions(options);

          // Store all keys for duplicate validation
          setAllCategoryKeys(data.all_keys || []);
        }
      } catch (error) {
        console.error("Error fetching categories:", error);
      } finally {
        setLoadingCategories(false);
      }
    };

    const fetchQuestionnaires = async () => {
      if (!show) return;
      setLoadingQuestionnaires(true);
      try {
        const response = await get_questionnaires();
        const data = response?.statusCode?.response_data?.data;
        if (data && data.questionnaires) {
          const options = data.questionnaires.map(q => ({
            value: q.name,
            label: q.name
          }));
          options.push({ value: "custom", label: "Custom (Enter your own)" });
          setQuestionnaireOptions(options);
        }
      } catch (error) {
        console.error("Error fetching questionnaires:", error);
      } finally {
        setLoadingQuestionnaires(false);
      }
    };

    fetchCategories();
    fetchQuestionnaires();
  }, [show]);

  // Populate form when editing
  useEffect(() => {
    if (show) {
      // Clear messages when opening
      setResMsg("");
      setErrResMsg("");
      setErrors({});
    }

    if (editingQuestion && show) {
      const responseTypeValue = responseTypeMap[editingQuestion.response_type_display || editingQuestion.response_type] || "yes_no";

      // Check if category exists in organization categories
      const categoryKey = editingQuestion.category || "";
      const existsInOptions = categoryOptions.some(opt => opt.value === categoryKey && opt.value !== "custom");

      setIsCustomCategory(!existsInOptions && categoryKey !== "");
      // Check if questionnaire exists in options
      const questionnaireName = editingQuestion.questionnaire_name || "";
      const existsInQuestionnaires = questionnaireOptions.some(opt => opt.value === questionnaireName && opt.value !== "custom");
      setIsCustomQuestionnaire(!existsInQuestionnaires && questionnaireName !== "");

      setFormData({
        questionnaire_name: questionnaireName,
        category: categoryKey,
        sub_category: editingQuestion.sub_category || "",
        question_text: editingQuestion.question_text || "",
        response_type: responseTypeValue,
        expected_evidence: editingQuestion.expected_evidence || "",
        explanation: editingQuestion.explanation || "",
        weight: editingQuestion.weight || editingQuestion.weight_display || 1,
        multi_select_options: editingQuestion.multi_select_options && Array.isArray(editingQuestion.multi_select_options)
          ? editingQuestion.multi_select_options
          : (responseTypeValue === "multi_select" ? ["", ""] : []),
      });
    } else if (!editingQuestion && show) {
      // Reset form for new question
      const firstOption = categoryOptions.length > 0 ? categoryOptions[0].value : "";
      const startAsCustom = firstOption === "custom";

      setIsCustomCategory(startAsCustom);
      setFormData({
        questionnaire_name: "",
        category: startAsCustom ? "" : firstOption,
        sub_category: "",
        question_text: "",
        response_type: "yes_no",
        expected_evidence: "",
        explanation: "",
        weight: 1,
        multi_select_options: [],
      });
    }
  }, [editingQuestion, show, categoryOptions]);

  const handleChange = (e) => {
    const { name, value } = e.target;

    // Handle category - distinguish between dropdown selection and text input
    if (name === "category") {
      // If we're in custom category mode, just update the value (user is typing)
      if (isCustomCategory) {
        setFormData(prev => ({ ...prev, category: value }));
        if (errors.category) {
          setErrors(prev => ({ ...prev, category: "" }));
        }
        return;
      }
      // Otherwise, it's a dropdown selection
      if (value === "custom") {
        setIsCustomCategory(true);
        setFormData(prev => ({ ...prev, category: "" }));
      } else {
        setIsCustomCategory(false);
        setFormData(prev => ({ ...prev, category: value }));
      }
      if (errors.category) {
        setErrors(prev => ({ ...prev, category: "" }));
      }
      return;
    }

    // Handle questionnaire_name - distinguish between dropdown selection and text input
    if (name === "questionnaire_name") {
      if (isCustomQuestionnaire) {
        setFormData(prev => ({ ...prev, questionnaire_name: value }));
        return;
      }
      if (value === "custom") {
        setIsCustomQuestionnaire(true);
        setFormData(prev => ({ ...prev, questionnaire_name: "" }));
      } else {
        setIsCustomQuestionnaire(false);
        setFormData(prev => ({ ...prev, questionnaire_name: value }));
      }
      return;
    }

    setFormData((prev) => {
      const updated = {
        ...prev,
        [name]: name === "weight" ? parseInt(value) : value,
      };
      // Initialize or reset multi_select_options based on response_type
      if (name === "response_type") {
        if (value === "multi_select") {
          // Initialize with 2 empty options if switching to multi_select
          updated.multi_select_options =
            prev.multi_select_options.length === 0
              ? ["", ""]
              : prev.multi_select_options;
        } else {
          // Clear when switching away from multi_select
          updated.multi_select_options = [];
        }
      }
      return updated;
    });
    // Clear error for this field
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  const handleMultiSelectOptionChange = (index, value) => {
    setFormData((prev) => {
      const updated = [...prev.multi_select_options];
      updated[index] = value;
      return {
        ...prev,
        multi_select_options: updated,
      };
    });
    // Clear error for multi_select_options
    if (errors.multi_select_options) {
      setErrors((prev) => ({ ...prev, multi_select_options: "" }));
    }
  };

  const handleAddMultiSelectOption = () => {
    setFormData((prev) => ({
      ...prev,
      multi_select_options: [...prev.multi_select_options, ""],
    }));
  };

  const handleRemoveMultiSelectOption = (index) => {
    setFormData((prev) => ({
      ...prev,
      multi_select_options: prev.multi_select_options.filter((_, i) => i !== index),
    }));
    // Clear error for multi_select_options
    if (errors.multi_select_options) {
      setErrors((prev) => ({ ...prev, multi_select_options: "" }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.question_text.trim()) {
      newErrors.question_text = "Question text is required";
    }
    // Validate custom category
    if (isCustomCategory) {
      if (!formData.category.trim()) {
        newErrors.category = "Custom category name is required";
      } else {
        // Check for duplicate category key (convert to lowercase and replace spaces with underscores)
        const categoryKey = formData.category.trim().toLowerCase().replace(/\s+/g, '_');
        if (allCategoryKeys.includes(categoryKey)) {
          newErrors.category = "This category already exists. Please choose a different name.";
        }
      }
    }

    // Validate custom questionnaire name
    if (isCustomQuestionnaire) {
      if (formData.questionnaire_name && formData.questionnaire_name.trim().toLowerCase() === 'default') {
        newErrors.questionnaire_name = "Questionnaire name cannot be 'Default'. Please choose a different name.";
      }
    }

    // Validate multi_select_options
    if (formData.response_type === "multi_select") {
      const validOptions = formData.multi_select_options.filter(
        (opt) => opt && opt.trim().length > 0
      );
      if (validOptions.length < 2) {
        newErrors.multi_select_options =
          "At least 2 options are required for multi-select questions";
      } else {
        // Validate each option length
        const invalidOptions = validOptions.filter(
          (opt) => opt.trim().length < 1 || opt.trim().length > 255
        );
        if (invalidOptions.length > 0) {
          newErrors.multi_select_options =
            "Each option must be between 1 and 255 characters";
        }
      }
    }
    return newErrors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const validationErrors = validateForm();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    setLoading(true);
    setResMsg("");
    setErrResMsg("");

    try {
      // Filter out empty options and trim them
      const validMultiSelectOptions =
        formData.response_type === "multi_select"
          ? formData.multi_select_options
            .filter((opt) => opt && opt.trim().length > 0)
            .map((opt) => opt.trim())
          : null;

      const payload = {
        question_text: formData.question_text,
        response_type: formData.response_type,
        weight: formData.weight,
        ...(formData.questionnaire_name && {
          questionnaire_name: formData.questionnaire_name,
        }),
        ...(formData.category && {
          category: formData.category,
        }),
        ...(formData.sub_category && {
          sub_category: formData.sub_category,
        }),
        ...(formData.expected_evidence && {
          expected_evidence: formData.expected_evidence,
        }),
        ...(formData.explanation && {
          explanation: formData.explanation,
        }),
        ...(formData.response_type === "multi_select" &&
          validMultiSelectOptions &&
          validMultiSelectOptions.length > 0 && {
          multi_select_options: validMultiSelectOptions,
        }),
      };

      let response;
      if (editingQuestion && editingQuestion.id) {
        // Edit existing question
        response = await editThirdpartyQuestionsService(`${editingQuestion.id}/`, payload);
      } else {
        // Create new question
        response = await createThirdpartyQuestionsService(payload);
      }

      const successMessage =
        response?.statusCode?.response_data?.message ||
        response?.statusCode?.message ||
        (editingQuestion ? "Question updated successfully" : "Question created successfully");
      const errorMessage =
        response?.errors?.message ||
        response?.error?.message ||
        response?.statusCode?.error?.message;

      if (successMessage && !errorMessage) {
        setResMsg(successMessage);
        // Reset form
        setIsCustomCategory(false);
        setFormData({
          questionnaire_name: "",
          category: "default",
          sub_category: "",
          question_text: "",
          response_type: "yes_no",
          expected_evidence: "",
          explanation: "",
          weight: 1,
          multi_select_options: [],
        });
        setErrors({});
        onSuccess && onSuccess();
      } else if (errorMessage) {
        setErrResMsg(errorMessage);
      } else {
        setErrResMsg(editingQuestion ? "Failed to update question. Please try again." : "Failed to create question. Please try again.");
      }
    } catch (error) {
      console.error("Error creating TPRM question:", error);
      setErrResMsg("Failed to create question. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setIsCustomCategory(false);
    setIsCustomQuestionnaire(false);
    setFormData({
      questionnaire_name: "",
      category: "default",
      sub_category: "",
      question_text: "",
      response_type: "yes_no",
      expected_evidence: "",
      explanation: "",
      weight: 1,
      multi_select_options: [],
    });
    setErrors({});
    setResMsg("");
    setErrResMsg("");
    onHide();
  };

  return (
    <Modal className="cstm_modal" show={show} onHide={handleClose} centered size="lg">
      <Modal.Header closeButton>
        <Modal.Title>{editingQuestion ? "Edit TPRM Question" : "Create TPRM Question"}</Modal.Title>
      </Modal.Header>
      <Modal.Body className="p-4">
        <Form onSubmit={handleSubmit}>
          {/* Questionnaire Name */}
          <Form.Group className="mb-3">
            <Form.Label className="font_500 color_white">Questionnaire Name</Form.Label>
            {isCustomQuestionnaire ? (
              <div className="d-flex gap-2">
                <div className="w-100">
                  <Form.Control
                    type="text"
                    name="questionnaire_name"
                    value={formData.questionnaire_name}
                    onChange={handleChange}
                    placeholder="e.g., Default, Cloud Security Assessment, SOC2 Audit"
                    maxLength={200}
                    isInvalid={!!errors.questionnaire_name}
                  />
                  <Form.Control.Feedback type="invalid">
                    {errors.questionnaire_name}
                  </Form.Control.Feedback>
                </div>
                {questionnaireOptions.length > 0 && (
                  <button
                    type="button"
                    className="btn btn-outline-secondary"
                    onClick={() => {
                      setIsCustomQuestionnaire(false);
                      setFormData(prev => ({ ...prev, questionnaire_name: questionnaireOptions.length > 0 ? questionnaireOptions[0].value : "" }));
                    }}
                    style={{ height: 'fit-content' }}
                  >
                    Cancel
                  </button>
                )}
              </div>
            ) : (
              <>
                <Form.Select
                  name="questionnaire_name"
                  value={formData.questionnaire_name}
                  onChange={handleChange}
                  disabled={loadingQuestionnaires}
                  isInvalid={!!errors.questionnaire_name}
                >
                  {loadingQuestionnaires ? (
                    <option value="">Loading questionnaires...</option>
                  ) : questionnaireOptions.length === 0 ? (
                    <option value="">No questionnaires available</option>
                  ) : (
                    questionnaireOptions.map((opt) => (
                      <option key={opt.value} value={opt.value}>
                        {opt.label}
                      </option>
                    ))
                  )}
                </Form.Select>
                <Form.Control.Feedback type="invalid">
                  {errors.questionnaire_name}
                </Form.Control.Feedback>
              </>
            )}
          </Form.Group>

          {/* Category */}
          <Form.Group className="mb-3">
            <Form.Label className="font_500 color_white">Category</Form.Label>
            {isCustomCategory ? (
              <div className="d-flex gap-2">
                <Form.Control
                  type="text"
                  name="category"
                  value={formData.category}
                  onChange={handleChange}
                  placeholder="Enter your custom category name"
                  isInvalid={!!errors.category}
                />
                {categoryOptions.length > 0 && (
                  <button
                    type="button"
                    className="btn btn-outline-secondary"
                    onClick={() => {
                      setIsCustomCategory(false);
                      setFormData(prev => ({ ...prev, category: categoryOptions.length > 0 ? categoryOptions[0].value : "" }));
                    }}
                  >
                    Cancel
                  </button>
                )}
              </div>
            ) : (
              <Form.Select
                name="category"
                value={formData.category}
                onChange={handleChange}
                isInvalid={!!errors.category}
                disabled={loadingCategories}
              >
                {loadingCategories ? (
                  <option value="">Loading categories...</option>
                ) : categoryOptions.length === 0 ? (
                  <option value="">No categories available</option>
                ) : (
                  categoryOptions.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))
                )}
              </Form.Select>
            )}
            <Form.Control.Feedback type="invalid">
              {errors.category}
            </Form.Control.Feedback>
          </Form.Group>

          {/* Sub Category */}
          <Form.Group className="mb-3">
            <Form.Label className="font_500 color_white">Sub Category</Form.Label>
            <Form.Control
              type="text"
              name="sub_category"
              value={formData.sub_category}
              onChange={handleChange}
              placeholder="Enter sub category"
            />
          </Form.Group>



          {/* Question Text */}
          <Form.Group className="mb-3">
            <Form.Label className="font_500 color_white">
              Question Text *
            </Form.Label>
            <Form.Control
              as="textarea"
              rows={3}
              name="question_text"
              value={formData.question_text}
              onChange={handleChange}
              placeholder="Enter the question text"
              isInvalid={!!errors.question_text}
            />
            <Form.Control.Feedback type="invalid">
              {errors.question_text}
            </Form.Control.Feedback>
          </Form.Group>

          {/* Response Type */}
          <Form.Group className="mb-3">
            <Form.Label className="font_500 color_white">
              Response Type *
            </Form.Label>
            <Form.Select
              name="response_type"
              value={formData.response_type}
              onChange={handleChange}
              isInvalid={!!errors.response_type}
            >
              {RESPONSE_TYPE_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </Form.Select>
            <Form.Control.Feedback type="invalid">
              {errors.response_type}
            </Form.Control.Feedback>
          </Form.Group>

          {/* Expected Evidence */}
          <Form.Group className="mb-3">
            <Form.Label className="font_500 color_white">
              Expected Evidence
            </Form.Label>
            <Form.Control
              type="text"
              name="expected_evidence"
              value={formData.expected_evidence}
              onChange={handleChange}
              placeholder="e.g., Data flow diagram, architecture note"
            />
          </Form.Group>

          {/* Explanation */}
          <Form.Group className="mb-3">
            <Form.Label className="font_500 color_white">
              Explanation
            </Form.Label>
            <Form.Control
              as="textarea"
              rows={2}
              name="explanation"
              value={formData.explanation}
              onChange={handleChange}
              placeholder="AI-generated explanation for the question to guide vendors"
            />
          </Form.Group>

          {/* Weight */}
          <Form.Group className="mb-3">
            <Form.Label className="font_500 color_white">Weight *</Form.Label>
            <Form.Select
              name="weight"
              value={formData.weight}
              onChange={handleChange}
              isInvalid={!!errors.weight}
            >
              {WEIGHT_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </Form.Select>
            <Form.Control.Feedback type="invalid">
              {errors.weight}
            </Form.Control.Feedback>
          </Form.Group>

          {/* Multi-select Options - Only show if response_type is multi_select */}
          {formData.response_type === "multi_select" && (
            <Form.Group className="mb-3">
              <Form.Label className="font_500 color_white">
                Multi-select Options * (Minimum 2 required)
              </Form.Label>
              {formData.multi_select_options.map((option, index) => (
                <div key={index} className="d-flex mb-2">
                  <Form.Control
                    type="text"
                    value={option}
                    onChange={(e) =>
                      handleMultiSelectOptionChange(index, e.target.value)
                    }
                    placeholder={`Option ${index + 1}`}
                    maxLength={255}
                    className="me-2"
                  />
                  <Button
                    variant="outline-danger"
                    onClick={() => handleRemoveMultiSelectOption(index)}
                    disabled={formData.multi_select_options.length <= 2}
                  >
                    Remove
                  </Button>
                </div>
              ))}
              <Button
                variant="outline-primary"
                onClick={handleAddMultiSelectOption}
                className="mt-2"
              >
                Add Option
              </Button>
              {errors.multi_select_options && (
                <div className="text-danger mt-2">
                  {errors.multi_select_options}
                </div>
              )}
              <Form.Text className="text-white d-block mt-2">
                Each option must be between 1 and 255 characters. At least 2
                options are required.
              </Form.Text>
            </Form.Group>
          )}

          {/* Messages */}
          {resMsg && <p className="text-success mt-2">{resMsg}</p>}
          {errResMsg && <p className="text-danger mt-2">{errResMsg}</p>}

          {/* Buttons */}
          <div className="d-flex justify-content-end mt-4 mb-4">
            <Button
              variant="outline-secondary"
              onClick={handleClose}
              className="me-2"
              disabled={loading}
            >
              Cancel
            </Button>
            <Button variant="primary" type="submit" disabled={loading}>
              {loading ? <ButtonLoader /> : (editingQuestion ? "Update Question" : "Create Question")}
            </Button>
          </div>
        </Form>
      </Modal.Body>
    </Modal>
  );
}

export default CreateTPRMQuestionModal;

